import { useEffect } from "react";
import AppLayout from "@/layouts/AppLayout";
import { Outlet, useLocation } from "react-router-dom";
import { PersonObjectHeading } from "@/features/person/components/PersonObjectHeading";
import TabLayout from "./_TabLayout";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";
import Breadcrumb from "@/components/Breadcrumb";
import useCapabilities, { useDenyAuthorization } from "@/hooks/useCapabilities";
import { useFlagsLoaded } from "@/hooks/useFeatureFlags";
import { usePersonErrorHandling } from "../_components/usePersonErrorHandling";
import PersonNotFoundErrorBoundary from "../_components/PersonNotFoundErrorBoundary";
import { useCanAccessPersonBenefits } from "./_utils";

export default function PersonBenefitsLayout() {
  const { loaded } = useCapabilities();
  const flagsLoaded = useFlagsLoaded();
  const canAccessBenefits = useCanAccessPersonBenefits();
  const denyAuthorization = useDenyAuthorization();

  useEffect(() => {
    if (!loaded || !flagsLoaded) return;
    if (!canAccessBenefits) denyAuthorization();
  }, [loaded, flagsLoaded, canAccessBenefits, denyAuthorization]);

  const location = useLocation();
  const isUpdateBeneficiariesPage = location.pathname.includes("/beneficiaries/update/");
  const { isCompactLayout: isMobile } = useIsCompactLayout();
  const { pgaId, navLinks, shouldRenderError } = usePersonErrorHandling();

  if (shouldRenderError) {
    return (
      <AppLayout navLinks={[]} interior bottomBar>
        <PersonNotFoundErrorBoundary />
      </AppLayout>
    );
  }

  if (isUpdateBeneficiariesPage) {
    return <Outlet />;
  }

  const renderDesktopLayout = () => (
    <>
      <PersonObjectHeading pgaId={pgaId!} />
      <TabLayout>
        <Outlet />
      </TabLayout>
    </>
  );

  const renderMobileLayout = () => (
    <>
      <Breadcrumb
        title="Benefits"
        compactOnly
        compactTitle="Benefits"
        backLink={{ to: "/people/:pgaId", params: { pgaId: pgaId! } }}
      />
      <PersonObjectHeading pgaId={pgaId!} />
      <TabLayout>
        <Outlet />
      </TabLayout>
    </>
  );

  return (
    <AppLayout navLinks={navLinks} interior bottomBar>
      {isMobile ? renderMobileLayout() : renderDesktopLayout()}
    </AppLayout>
  );
}
